import {useMemo, useState} from 'react'

import {IconArrowRightSmall} from '@defign/asset'
import classNames from 'classnames/bind'

import EntryRow from './EntryRow'
import styles from './LogValue.module.scss'

import type {LogValueNode} from '../../type'
import type {FC, ReactNode} from 'react'

const cx = classNames.bind(styles)

interface ExpandableProps {
    node: LogValueNode
    depth: number
    renderValue: (node: LogValueNode, depth: number) => ReactNode
}

const Expandable: FC<ExpandableProps> = ({node, depth, renderValue}) => {
    const [isOpen, setIsOpen] = useState(depth < 1)

    const label = useMemo(() => {
        if (node.type === 'array') {
            return `Array(${node.arrayLength ?? node.children?.length ?? 0})`
        }
        if (node.objectName) {
            return `${node.objectName} {…}`
        }

        return '{…}'
    }, [node])

    return (
        <span className={cx('expandable')}>
            <button type="button" className={cx('toggle', {open: isOpen})} onClick={() => setIsOpen((prev) => !prev)}>
                <div className={cx('arrow')}>
                    <IconArrowRightSmall width={12} height={12} />
                </div>
                <span className={cx('toggleLabel')}>{isOpen ? node.preview : label}</span>
            </button>

            {isOpen && Array.isArray(node.children) && node.children.length > 0 && (
                <div className={cx('children')}>
                    {node.children.map((entry) => (
                        <EntryRow key={entry.key} entry={entry} depth={depth + 1} renderValue={renderValue} />
                    ))}
                </div>
            )}

            {isOpen && (!Array.isArray(node.children) || node.children.length === 0) && (
                <span className={cx('empty')}>
                    {node.type === 'array' && '[]'}
                    {node.type === 'object' && '{}'}
                    {node.type === 'map' && 'Map(0) {}'}
                    {node.type === 'set' && 'Set(0) {}'}
                </span>
            )}
        </span>
    )
}

export default Expandable
